<template>
  <PageWrapper
    :active-section="SECTION_COMMUNITY"
    :active-page="PAGE_NETWORK_OVERVIEW"
    :breadcrumbs="breadcrumbs"
    :page-name="networkName"
    align="left"
    width="full"
  >
    <ProfileWrapper
      :splash-image="networkSplashImage"
      :logo-image="networkLogo"
      :title="networkName"
      :details="networkDescription || 'Click here to add a description for your network'"
      :can-edit-details="isOwner"
      :can-upload-splash="isOwner"
      :can-edit-title="isOwner"
      :can-upload-logo="isOwner"
      :is-loading-logo="isLoadingUploadLogo"
      :is-loading-splash="isLoadingUploadSplashImage"
      @click:save-title="saveNetworkName"
      @click:save-details="saveNetworkDescription"
      @click:logo-image="triggerUploadLogo"
      @click:splash-image="triggerUploadSplash"
    >
      <template #menu>
        <iButton variant="primary">
          Subscribe Now
        </iButton>
      </template>
      <iColumn v-if="hasMonthlyPrice">
        <iSpace :height="5" />
        <iSubHeading font-size="large">
          Price {{ formattedMonthlyPrice }} per {{ formattedBillingFrequency }}
        </iSubHeading>
      </iColumn>
      <iColumn v-if="!isOwner" gap="small">
        <iSubHeading>Apply To Join</iSubHeading>
        <template v-if="!network.invite_only">
          <iText>
            This network is accepting applications. Apply now if you have a publication that would be a good fit.
          </iText>
          <iButton variant="primary" @click="openApplyModal">
            Apply Now
          </iButton>

          <template v-if="useMyNetworkPublicationApplicationsFeedStore().feed.length > 0">
            <iSpace :height="5" />
            <iSubHeading>
              My Applications
            </iSubHeading>
          </template>
          <SmartFeed
            :source="useMyNetworkPublicationApplicationsFeedStore"
            load-more-behavior="auto"
            default-feed-layout="carousel"
            default-entity-size="large"
          />
        </template>
        <iText v-else variant="subtle">
          This network is not currently accepting applications for new publications
        </iText>
      </iColumn>
      <iColumn v-if="isOwner" gap="large" class="network-preview">
        <NetworkSubscriptionPlanCard
          :subscription-plan="network.subscription_plan"
        />
      </iColumn>
      <iColumn v-if="isOwner" gap="large">
        <iSpace :height="10" />
        <iRow align="between" vertical-align="middle">
          <iSubHeading>Applicants</iSubHeading>
          <iRow vertical-align="middle" gap="small" wrap="nowrap">
            <iColumn width="hug">
              <iText :wrap="false">
                Accepting Applications
              </iText>
            </iColumn>
            <iColumn width="hug">
              <iRadioBar
                v-model="allowApplicationsValue"
                width="hug"
                :options="yesNoOptions"
                @update:model-value="toggleApplications"
              />
            </iColumn>
            <iColumn width="hug">
              <iLoadingSpinner v-if="isLoadingEditNetwork" size="small" />
            </iColumn>
          </iRow>
        </iRow>
        <SmartFeed
          :source="useNetworkApplicantsAppliedFeedStore"
          load-more-behavior="auto"
          default-feed-layout="carousel"
          default-entity-size="small"
          :show-filters="false"
        >
          <template #empty-state>
            <iText variant="subtle">
              {{ applicantEmptyMessage }}
            </iText>
          </template>
        </SmartFeed>
        <ReviewApplicationModal />
      </iColumn>
      <iColumn v-if="isOwner" gap="large">
        <iRow align="between" vertical-align="middle">
          <iSubHeading>Pending Invites</iSubHeading>
        </iRow>
        <SmartFeed
          :source="useNetworkApplicantsInvitedFeedStore"
          load-more-behavior="auto"
          default-feed-layout="carousel"
          default-entity-size="small"
          :show-filters="false"
        >
          <template #empty-state>
            <iText variant="subtle">
              {{ applicantEmptyMessage }}
            </iText>
          </template>
        </SmartFeed>
        <ReviewInviteModal />
      </iColumn>

      <iColumn gap="large">
        <iSubHeading>Publications</iSubHeading>
        <SmartFeed
          :show-filters="false"
          :source="useNetworkPublicationsFeedStore"
          load-more-behavior="auto"
          default-feed-layout="grid"
          default-entity-size="large"
        >
          <template #empty-state>
            <iColumn width="fill">
              <iParagraph
                variant="subtle"
                text-align="center"
              >
                Once there are publications in this network they will appear here
              </iParagraph>
            </iColumn>
          </template>
        </SmartFeed>
      </iColumn>
      <ApplyToNetworkModal :network-id="networkId" :publication-id="0" />
      <iUploader ref="logo-uploader" @upload-file="uploadLogo" />
      <iUploader ref="splash-uploader" @upload-file="uploadSplash" />
    </ProfileWrapper>
  </PageWrapper>
</template>

<script>
import PageWrapper from "@/components/PageWrapper";
import ProfileWrapper from "@/components/publication/ProfileWrapper";
import ApplyToNetworkModal from "@/components/networks/ApplyToNetworkModal.vue";
import ReviewApplicationModal from "@/components/networks/ReviewApplicationModal.vue";
import SmartFeed from "SmartFeed/components/SmartFeedV2.vue";
import { useNetworkStore } from "@/stores/network-store";
import { useNetworkPublicationsFeedStore } from "@/smart-feed/stores/feed/network-publications-feed-store";
import { PAGE_NETWORK_OVERVIEW, SECTION_COMMUNITY } from "@/constants/nav-constants";
import { mapActions, mapState } from "pinia";
import { useGeneralStore } from "%/stores/general-store";
import { useNetworkApplicantsInvitedFeedStore } from "@/smart-feed/stores/feed/network-applicants-invited-feed-store";
import { useNetworkApplicantsAppliedFeedStore } from "@/smart-feed/stores/feed/network-applicants-applied-feed-store";
import ReviewInviteModal from "@/components/networks/ReviewInviteModal.vue";
import { formatCurrency } from "@bloglovin/vue-framework";
import NetworkSubscriptionPlanCard from "@/components/networks/NetworkSubscriptionPlanCard.vue";
import { BILLING_PERIODS_NOUN_TEXT } from "~/constants/subscription-constants";
import { useMyNetworkPublicationApplicationsFeedStore } from "@/smart-feed/stores/feed/my-network-publication-applications-feed-store";

export default {
  name: "NetworkOverview",
  components: {
    ReviewInviteModal,
    PageWrapper,
    ProfileWrapper,
    SmartFeed,
    ApplyToNetworkModal,
    ReviewApplicationModal,
    NetworkSubscriptionPlanCard,
  },
  props: {
    networkId: { type: Number, required: true },
    network: { type: Object, required: true },
  },
  data() {
    return {
      SECTION_COMMUNITY,
      PAGE_NETWORK_OVERVIEW,
      allowApplications: !this.network.invite_only,
      allowApplicationsValue: !this.network.invite_only ? "yes" : "no",
      isToggling: false,
      yesNoOptions: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    };
  },
  computed: {
    ...mapState(useGeneralStore, ["orgId"]),
    ...mapState(useNetworkStore, [
      "network",
      "isLoadingEditNetwork",
      "isLoadingUploadLogo",
      "isLoadingUploadSplashImage",
      "networkLogo",
      "networkSplashImage",
      "networkName",
      "networkDescription",
    ]),
    breadcrumbs() {
      return [
        { url: "/", label: "Home" },
        { url: "/community", label: "Community" },
        { url: "/community/networks", label: "Networks" },
      ];
    },
    isOwner() {
      return this.network?.owner_org_id === this.orgId;
    },
    applicantEmptyMessage() {
      if (this.allowApplications) {
        return "You have no applications pending at this time";
      }
      return "You are not accepting applications at this time";
    },
    hasMonthlyPrice() {
      return (
        this.network?.subscription_plan?.price &&
          parseFloat(this.network.subscription_plan.price) > 0
      );
    },
    formattedMonthlyPrice() {
      if (!this.hasMonthlyPrice) {
        return "";
      }
      return formatCurrency(
        parseFloat(this.network.subscription_plan.price),
        this.network.subscription_plan.currency_code || "USD",
        "en-US",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
      );
    },
    formattedBillingFrequency() {
      const freq =
          (this.network?.subscription_plan?.billing_frequency || "").toLowerCase();
      const noun = BILLING_PERIODS_NOUN_TEXT[freq] || freq;
      return noun ? noun.charAt(0).toUpperCase() + noun.slice(1) : "";
    },
  },
  watch: {
    "network.invite_only"(newVal) {
      this.allowApplications = !newVal;
      this.allowApplicationsValue = !newVal ? "yes" : "no";
    },
  },
  methods: {
    useNetworkApplicantsAppliedFeedStore,
    useNetworkApplicantsInvitedFeedStore,
    ...mapActions(useNetworkStore, {
      openApplyToNetworkModal: "openApplyToNetworkModal",
      enableApplications: "enableApplications",
      disableApplications: "disableApplications",
      uploadNetworkLogo: "uploadLogo",
      uploadNetworkSplashImage: "uploadSplashImage",
      updateNetworkName: "saveNetworkName",
      updateNetworkDescription: "saveNetworkDescription",
    }),
    openApplyModal() {
      this.openApplyToNetworkModal();
    },
    toggleApplications () {
      this.allowApplications = this.allowApplicationsValue === "yes";
      if (this.allowApplications) {
        this.enableApplications(this.networkId);
      } else {
        this.disableApplications(this.networkId);
      }
    },
    triggerUploadLogo() {
      this.$refs["logo-uploader"].$refs.file.click();
    },
    triggerUploadSplash() {
      this.$refs["splash-uploader"].$refs.file.click();
    },
    uploadLogo(file) {
      this.uploadNetworkLogo(file);
    },
    uploadSplash(file) {
      this.uploadNetworkSplashImage(file);
    },
    saveNetworkName(name) {
      this.updateNetworkName(this.networkId, name );
    },
    saveNetworkDescription(description) {
      this.updateNetworkDescription(this.networkId, description);
    },
    useNetworkPublicationsFeedStore,
    useMyNetworkPublicationApplicationsFeedStore,
  },
};
</script>

<style scoped>
:deep(.network-preview){
  position: absolute;
  max-width: 300px;
  right: 30px;
}
</style>
